import { getURLHostname } from 'lib/utils/url';
import validate from 'lib/utils/validationRules';
import { formatDateToCustomDateFormat } from 'lib/utils/date';

import { DOCS_URL } from 'root/.env';

import { STREAMER_HOSTNAME_LIST } from 'definitions/streamer/widgets';

export default {
  userCanUseCabinet(state) {
    if (!state.PROJECT_INFO.status) {
      return false;
    }

    return state.PROJECT_INFO.status === 'ACTIVE' || state.PROJECT_INFO.status === 'PENDING';
  },

  merchantIsInactive(state) {
    return state.MERCHANT_INFO.status === 'INACTIVE';
  },

  isNeedIdentification(state) {
    return state.MERCHANT_INFO.needIdentification;
  },

  isShowSelfEmployedBindingNotification(state) {
    return state.MERCHANT_INFO.showSelfEmployedBindingNotification;
  },

  getMerchantTimezone(state) {
    return state.MERCHANT_INFO.timezone;
  },

  userProjectIsInactive(state) {
    return state.PROJECT_INFO.status === 'INACTIVE';
  },

  projectValidated(state) {
    return state.PROJECT_INFO.validated;
  },

  projectPaymentsDisabled(state) {
    return !state.PROJECT_INFO.paymentsEnabled;
  },

  projectPayoutsDisabled(state) {
    return !state.PROJECT_INFO.payoutsEnabled;
  },

  periodicRecurrentPaymentPermission(state) {
    return state.PROJECT_INFO.periodicRecurrentPaymentPermission;
  },

  recurrentPaymentPermission(state) {
    return state.PROJECT_INFO.recurrentPaymentPermission;
  },

  makerPublishingPermission(state) {
    return state.PROJECT_INFO.makerPublishingPermission;
  },

  isDesktop(state) {
    if (!state.WINDOW_SIZE) {
      return false;
    }

    let r = document.querySelectorAll(':root')[0];
    let s = window.getComputedStyle(r, null).getPropertyValue('font-size');

    return state.WINDOW_SIZE >= parseInt(s) * 64;
  },

  isDeviceSize(state) {
    if (!state.WINDOW_SIZE) {
      return false;
    }

    return {
      mobile: state.WINDOW_SIZE < 370,
      mobileBig: state.WINDOW_SIZE < 480,
      mobilePlus: state.WINDOW_SIZE < 768,
      laptop: state.WINDOW_SIZE > 767,
      tablet: state.WINDOW_SIZE < 1024,
      desktop: state.WINDOW_SIZE >= 1024,
    };
  },

  isMobile(state) {
    return state.DEVICE.isMobile;
  },

  getTransactions(state) {
    const { TRANSACTIONS } = state;
    const groupedByDate = {};

    TRANSACTIONS.result.forEach(transaction => {
      const keyToGroup = formatDateToCustomDateFormat(transaction.created_at, 'DD.MM.YYYY');

      groupedByDate[keyToGroup] = groupedByDate[keyToGroup] || [];
      groupedByDate[keyToGroup].push(transaction);
    });

    return groupedByDate;
  },

  payoutPermissionIsEnabled(state) {
    const { cardPayoutPermission } = state.PROJECT_INFO;

    if (validate.isUndefined(cardPayoutPermission)) {
      return false;
    }

    return cardPayoutPermission === 'ENABLED';
  },

  walletPayoutPermissionIsEnabled(state) {
    const { walletPayoutPermission } = state.PROJECT_INFO;

    if (validate.isUndefined(walletPayoutPermission)) {
      return false;
    }

    return walletPayoutPermission === 'ENABLED';
  },

  getLastCardBindingPage(state) {
    return state.LAST_CARD_BINDING_PAGE;
  },

  getSelfEmployedStatus(state) {
    return state.SELF_EMPLOYED.status;
  },

  isSelfEmployedActivated(state, getters) {
    return getters.getSelfEmployedStatus === 'ACTIVE';
  },

  isObsidianTheme(state) {
    return state.MERCHANT_INFO.theme === 'obsidian';
  },

  SELECTED_TRANSACTION(state) {
    return state.SELECTED_TRANSACTION;
  },

  SELF_EMPLOYED_INN(state) {
    if (!state.SELF_EMPLOYED.properties) {
      return null;
    }

    if (!state.SELF_EMPLOYED.properties.inn) {
      return null;
    }

    return state.SELF_EMPLOYED.properties.inn;
  },

  SELF_EMPLOYED_LOADING(state) {
    return state.SELF_EMPLOYED.loading;
  },

  SELF_EMPLOYED_NOTIFICATIONS_LOADING(state) {
    return state.SELF_EMPLOYED.notifications.loading;
  },

  SELF_EMPLOYED_NOTIFICATIONS_LIST(state) {
    return state.SELF_EMPLOYED.notifications.list;
  },

  SELF_EMPLOYED_NOTIFICATIONS_READED(state) {
    const notifications = state.SELF_EMPLOYED.notifications.list;
    return notifications.filter(notification => notification.status !== 'NEW');
  },

  SELF_EMPLOYED_NOTIFICATIONS_UNREADED(state) {
    const notifications = state.SELF_EMPLOYED.notifications.list;
    return notifications.filter(notification => notification.status === 'NEW');
  },

  SELF_EMPLOYED_NOTIFICATIONS_COUNT(state) {
    return state.SELF_EMPLOYED.notifications.count;
  },

  MERCHANT(state) {
    return state.MERCHANT_INFO || null;
  },

  PROJECT(state) {
    return state.PROJECT_INFO || {};
  },

  PROJECT_LIST(state) {
    return state.MERCHANT_INFO.projects || [];
  },

  getMerchant(state) {
    return state.MERCHANT_INFO || {};
  },

  getCurrentProject(state) {
    return state.PROJECT_INFO || {};
  },

  SUBSCRIPTION_LIST(state) {
    const { subscribers: subscriptions } = state.SUBSCRIPTIONS;

    if (validate.isEmptyArray(subscriptions)) {
      return [];
    }

    return subscriptions;
  },

  WRITE_OFF_OPTIONS(state) {
    return state.WRITE_OFF_OPTIONS;
  },

  MARKETPLACE_CATEGORIES(state) {
    return state.MARKETPLACE_CATEGORIES;
  },

  isPersonalProject(state) {
    return state.PROJECT_INFO.type === 'PERSONAL';
  },

  isPartnerProject(state) {
    return state.PROJECT_INFO.type === 'PARTNER';
  },

  getProjectPaymentOptions(state) {
    return state.PROJECT_INFO.paymentOptions;
  },

  getProjectPromoCode(state) {
    return state.PROJECT_INFO.promoCode;
  },

  isExternalCheckoutProvider(state) {
    const { PROJECT_INFO } = state;

    if (!PROJECT_INFO?.provider) {
      return false;
    }

    return PROJECT_INFO.provider?.code === '97ff3d29-4500-405f-98e2-d9f18bc423d1';
  },

  isCheckoutPay(state) {
    return state.PROJECT_INFO?.provider?.checkoutPay || false;
  },

  getShopKey(state) {
    return state.PROJECT_INFO?.provider?.shopKey || null;
  },

  isChangeCurrencyPermission(state) {
    return state.PROJECT_INFO.currencyChangePermission || false;
  },

  isShownSubscriptions(state, getters) {
    const { subscriberCount } = state.PROJECT_INFO;
    return getters.periodicRecurrentPaymentPermission || (getters.recurrentPaymentPermission && subscriberCount.all > 0);
  },

  getCardBindingPrivacyPolicy(state) {
    const { currency } = state.PROJECT_INFO;

    if (currency?.code === 'UZS') {
      return `${DOCS_URL}/Offer_Binding-uz.pdf`;
    }

    return `${DOCS_URL}/Offer_Binding.pdf`;
  },

  isInactiveReferralProgram(state) {
    const { MERCHANT_INFO } = state;

    return MERCHANT_INFO.referralProgramStatus === 'INACTIVE';
  },

  getProjectLink(state) {
    const { PROJECT_INFO } = state;

    return PROJECT_INFO.link;
  },

  isStreamerProject(state, getters) {
    const urlHostname = getURLHostname(getters.getProjectLink);

    if (validate.isNull(urlHostname)) {
      return false;
    }

    return STREAMER_HOSTNAME_LIST.includes(urlHostname);
  },

  getCurrency(state) {
    return state.PROJECT_INFO?.currency || null;
  },

  getCurrencySign(state) {
    return state.PROJECT_INFO?.currency?.sign || '';
  },

  isEmptyCurrency(state, getters) {
    const { getCurrency } = getters;

    return validate.isNotObject(getCurrency);
  },

  isRussianCurrency(state, getters) {
    const { isEmptyCurrency, getCurrency } = getters;

    if (isEmptyCurrency) {
      return false;
    }

    return getCurrency.code === 'RUB';
  },

  isCurrencyCrypto(state, getters) {
    const { isEmptyCurrency, getCurrency } = getters;

    if (isEmptyCurrency) {
      return false;
    }

    return getCurrency.type === 'CRYPTO';
  },
};
